import { loadStripe } from "@stripe/stripe-js";
import { stripeConfig } from "./stripe-config";

// Initialize Stripe
let stripePromise;
const getStripe = () => {
  if (!stripePromise) {
    stripePromise = loadStripe(stripeConfig.publishableKey);
  }
  return stripePromise;
};

/**
 * SIMPLE METHOD: Use Stripe Payment Links (Recommended for MVP)
 *
 * This is the easiest way - just create Payment Links in Stripe Dashboard
 * and redirect users to them. No backend needed!
 *
 * To use this:
 * 1. Go to: https://dashboard.stripe.com/test/payment-links
 * 2. Click "New payment link"
 * 3. Select your Monthly product
 * 4. Copy the payment link URL
 * 5. Repeat for Annual product
 * 6. Update stripeConfig.paymentLinks with the URLs
 */
export const redirectToPaymentLink = (plan, userEmail, userId) => {
  const paymentLink =
    plan === "monthly"
      ? stripeConfig.paymentLinks.monthly
      : stripeConfig.paymentLinks.annual;

  // Check if payment link is configured
  if (!paymentLink || paymentLink.includes("REPLACE")) {
    alert(
      "⚠️ Payment links not set up yet!\n\n" +
        "To accept payments:\n" +
        "1. Go to https://dashboard.stripe.com/test/payment-links\n" +
        "2. Create payment links for Monthly ($9.99) and Annual ($99)\n" +
        "3. Update stripe-config.js with the link URLs\n\n" +
        "See STRIPE_SETUP_GUIDE.md for detailed instructions."
    );
    return;
  }

  try {
    // Add customer email as URL parameter
    const url = new URL(paymentLink);
    url.searchParams.set("prefilled_email", userEmail);
    url.searchParams.set("client_reference_id", userId);

    // Redirect to Stripe Payment Link
    window.location.href = url.toString();
  } catch (error) {
    console.error("Invalid payment link URL:", paymentLink);
    alert(
      "❌ Invalid payment link URL!\n\n" +
        "Please check that your payment link in stripe-config.js is a valid Stripe URL.\n" +
        "It should look like: https://buy.stripe.com/test_xxxxx"
    );
  }
};

/**
 * ADVANCED METHOD: Create Checkout Session via Cloud Function
 *
 * Use this when you have a backend set up.
 * This gives you more control over the checkout experience.
 */
export const createCheckoutSession = async (priceId, userEmail, userId) => {
  try {
    // Call your Cloud Function to create checkout session
    const response = await fetch(
      "YOUR_CLOUD_FUNCTION_URL/create-checkout-session",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          priceId,
          userEmail,
          userId,
          successUrl: `${window.location.origin}?payment=success`,
          cancelUrl: `${window.location.origin}?payment=canceled`,
        }),
      }
    );

    if (!response.ok) {
      throw new Error("Failed to create checkout session");
    }

    const { sessionId, url } = await response.json();

    if (url) {
      // Redirect to Stripe Checkout
      window.location.href = url;
    } else if (sessionId) {
      // Legacy: redirect using session ID
      const stripe = await getStripe();
      const { error } = await stripe.redirectToCheckout({ sessionId });
      if (error) {
        throw new Error(error.message);
      }
    }
  } catch (error) {
    console.error("Create session error:", error);
    throw error;
  }
};

/**
 * Handle post-payment actions
 * Call this when user returns from Stripe checkout
 */
export const handlePaymentSuccess = async (sessionId, updateUserTier) => {
  try {
    // In production, verify payment with your backend
    // For now, we'll trust the session ID presence

    if (sessionId) {
      // Update user to premium tier
      await updateUserTier("premium");
      return true;
    }
    return false;
  } catch (error) {
    console.error("Payment success handler error:", error);
    return false;
  }
};

/**
 * Check payment status from URL params
 * Call this on app load to handle return from Stripe
 */
export const checkPaymentStatus = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const paymentStatus = urlParams.get("payment");
  const sessionId = urlParams.get("session_id");

  return {
    success: paymentStatus === "success",
    canceled: paymentStatus === "canceled",
    sessionId,
  };
};
